import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

export default function PayPalSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    executePayment();
  }, []);

  const executePayment = async () => {
    const paymentId = searchParams.get('paymentId');
    const PayerID = searchParams.get('PayerID');
    const order_id = searchParams.get('order_id');
    const isGuest = searchParams.get('guest') === 'true';

    if (!paymentId || !PayerID || !order_id) {
      setStatus('error');
      setErrorMessage('Missing payment information');
      return;
    }

    setOrderId(order_id);

    try {
      const response = await api.get('/checkout/paypal/execute', {
        params: {
          paymentId,
          PayerID,
          order_id
        }
      });

      if (response.data.status === 'success') {
        setStatus('success');
        // Clear guest cart if applicable
        if (isGuest) {
          localStorage.removeItem('guestCart');
        }
      } else {
        setStatus('error');
        setErrorMessage('Payment could not be completed');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to process payment');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <Header />
      
      <div className="container mx-auto px-6 py-24">
        <div className="max-w-lg mx-auto text-center">
          {status === 'processing' && (
            <div data-testid="paypal-processing">
              <Loader className="h-16 w-16 text-[#4A6741] animate-spin mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Processing Payment
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment with PayPal...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="paypal-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Payment Successful!
              </h1>
              <p className="text-gray-600 mb-2">
                Thank you for your order. Your payment has been processed successfully.
              </p>
              {orderId && (
                <p className="text-sm text-gray-500 mb-8">
                  Order ID: <span className="font-mono">{orderId}</span>
                </p>
              )}
              <div className="space-y-3">
                <button
                  onClick={() => navigate(tenantPath('/dashboard'))}
                  className="w-full h-12 px-6 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3d5636] transition-all"
                  data-testid="view-orders-btn"
                >
                  View My Orders
                </button>
                <button
                  onClick={() => navigate(tenantPath('/'))}
                  className="w-full h-12 px-6 bg-white border-2 border-[#4A6741] text-[#4A6741] font-bold uppercase tracking-wider rounded-lg hover:bg-[#4A6741] hover:text-white transition-all"
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="paypal-error">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-12 w-12 text-red-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Payment Failed
              </h1>
              <p className="text-gray-600 mb-2">
                {errorMessage || 'There was an issue processing your payment.'}
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Please try again or contact support if the problem persists.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => navigate(tenantPath('/cart'))}
                  className="w-full h-12 px-6 bg-[#4A6741] text-white font-bold uppercase tracking-wider rounded-lg hover:bg-[#3d5636] transition-all"
                  data-testid="return-to-cart-btn"
                >
                  Return to Cart
                </button>
                <button
                  onClick={() => navigate(tenantPath('/contact'))}
                  className="w-full h-12 px-6 bg-white border-2 border-gray-300 text-gray-700 font-bold uppercase tracking-wider rounded-lg hover:bg-gray-50 transition-all"
                  data-testid="contact-support-btn"
                >
                  Contact Support
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
